import { useMutation } from "@apollo/client";
import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";

import { DELETE_VCS_AGENT_POOL } from "../ItemPage/gql";
import { VcsAgentPoolView } from "../types";

type DeleteConfirmationProps = {
  vcsAgentPool: VcsAgentPoolView;
  refetchQueriesOnDelete?: string[];
  onDeleteSuccess?: () => void;
};

const VcsAgentPoolDeleteConfirmationModal = create(function VcsAgentPoolDeleteConfirmationModal({
  vcsAgentPool,
  onDeleteSuccess,
  refetchQueriesOnDelete,
}: DeleteConfirmationProps) {
  const modal = useModal();

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [deleteVCSAgentPool, { loading }] = useMutation(DELETE_VCS_AGENT_POOL, {
    awaitRefetchQueries: true,
    refetchQueries: refetchQueriesOnDelete,
  });

  const onConfirm = useCallback(async () => {
    try {
      await deleteVCSAgentPool({ variables: { id: vcsAgentPool.id } });
      reportSuccess({ message: "VCS agent pool was deleted" });

      modal.hide();
      onDeleteSuccess?.();
    } catch (e) {
      onError(e);
    }
  }, [deleteVCSAgentPool, onError, reportSuccess, onDeleteSuccess, vcsAgentPool.id, modal]);

  return (
    <DeleteConfirmationModal
      title="Delete VCS agent pool"
      onConfirm={onConfirm}
      isLoading={loading}
      isDismissable={!loading}
    >
      <Box direction="column" gap="large">
        <Typography tag="p" variant="p-body2">
          Deleting a VCS Agent Pool is an irreversible operation and you will not be able to reuse
          its credentials for other VCS Agent Pools.
        </Typography>
        <Typography tag="p" variant="p-body2">
          Are you sure you want to delete <strong>{vcsAgentPool.name}</strong> VCS Agent Pool?
        </Typography>
      </Box>
    </DeleteConfirmationModal>
  );
});

export const showVcsAgentPoolDeleteConfirmation = (props: DeleteConfirmationProps) =>
  show(VcsAgentPoolDeleteConfirmationModal, props);
